/**
 * Shared scene chrome: back-to-menu button, help button + overlay, and
 * leaderboard ("High Scores") button + overlay.
 *
 * Consolidates the identical glue that previously lived in FroggerScene,
 * SnakeScene, and PipelineScene. Each game now supplies only its game-id
 * and a `getHelp()` thunk that returns `{ title, sections[] }`.
 *
 * Canonical layout (chip-style top bar, BTN_Y = 55 → centred in the 110-px
 * tall bar so all three buttons line up vertically with the chip cards on
 * the left):
 *   - back button (pill, 300 wide):  right edge at game.width - 20
 *   - help button (60-px circle):    centred at game.width - 400
 *   - leaderboard button (60-px):    centred at game.width - 480
 */

import { createBackButton, createLangButton, BACK_W, LANG_W, LANG_GAP } from './shared.js';
import { createHelpButton } from './HelpButton.js';
import { createLeaderboardButton } from './LeaderboardButton.js';
import { HelpOverlay } from './HelpOverlay.js';
import { LeaderboardOverlay } from './LeaderboardOverlay.js';

const HELP_BTN_X_OFFSET = 400;
const LB_BTN_X_OFFSET = 480;
const BTN_Y = 55;
// Help / leaderboard chrome buttons are 60-px circles, so each extends
// 30 px left of its centre. Used to place the language pill clear of the
// whole right-edge cluster.
const CHROME_BTN_RADIUS = 30;

export class SceneChrome {
    /**
     * @param {{
     *   game: any,
     *   host: import('../../vendor/pixi.min.mjs').Container,
     *   overlayHost?: import('../../vendor/pixi.min.mjs').Container,
     *   gameId: string,
     *   getHelp: () => { title: string, sections: Array<{heading: string, body: string, color?: number}> },
     *   onBack?: () => void,
     *   onPause?: () => void,
     *   onResume?: () => void,
     * }} opts
     */
    constructor(opts) {
        this._game = opts.game;
        this._host = opts.host;
        this._overlayHost = opts.overlayHost || opts.host;
        this._gameId = opts.gameId;
        this._getHelp = opts.getHelp;
        this._onBack = opts.onBack || (() => this._game.sceneManager.switchScene('menu'));
        this._onPause = opts.onPause || (() => {});
        this._onResume = opts.onResume || (() => {});

        this._backBtn = null;
        this._langBtn = null;
        this._helpBtn = null;
        this._lbBtn = null;
        this._helpOverlay = null;
        this._lbOverlay = null;
    }

    attach() {
        this._backBtn = createBackButton(this._game, this._onBack);
        this._host.addChild(this._backBtn);

        // Help ("How to play") button — hidden for Frogger and Snake
        // per UX direction (those games have onboarding cards / start
        // areas that already explain the rules, so the in-game `?`
        // pill becomes redundant). TrainSim and Pipeline keep it
        // because their controls are denser and a refresher mid-run
        // is genuinely useful.
        const HELP_HIDDEN_GAMES = new Set(['frogger', 'snake']);
        if (!HELP_HIDDEN_GAMES.has(this._gameId)) {
            this._helpBtn = createHelpButton(
                this._game.width - HELP_BTN_X_OFFSET, BTN_Y,
                () => this._showHelp(),
            );
            this._host.addChild(this._helpBtn);
        }

        // Leaderboard button is hidden when leaderboards are off.
        // Two layers checked in order:
        //   1. Build-time `__BUILD_LEADERBOARDS__` — set by esbuild
        //      from `data/shared.config.js → leaderboardsEnabled.default`.
        //      `false` here means the bundle was compiled without
        //      leaderboard support, so this code path is dead and
        //      DCE'd out entirely (no `createLeaderboardButton` call,
        //      no LeaderboardOverlay class shipped).
        //   2. Runtime `availableLanguages` setting — the OptionsScene
        //      toggle. Same effect, but live-toggleable in dev builds.
        // Either layer being off hides the button EVERYWHERE: no
        // in-game button, no post-game submit dialog.
        const lbBuildEnabled = typeof globalThis.__BUILD_LEADERBOARDS__ !== 'undefined'
            ? globalThis.__BUILD_LEADERBOARDS__
            : true;
        const lbEnabled = lbBuildEnabled
            && this._game.theme?.getSetting?.('shared', 'leaderboardsEnabled', true) !== false;
        if (lbEnabled) {
            this._lbBtn = createLeaderboardButton(
                this._game.width - LB_BTN_X_OFFSET, BTN_Y,
                () => this._showLeaderboard(),
            );
            this._host.addChild(this._lbBtn);
        }

        // Language pill (HU ⇄ EN) — sits just LEFT of the whole right-edge
        // chrome cluster (back + help + leaderboard, whichever are present)
        // so it never overlaps them. Built only when the deployment allows
        // switching, mirroring the main menu's two-layer guard:
        //   1. Build-time `__BUILD_LANG_MODE__` — esbuild define from
        //      `data/shared.config.js → availableLanguages.default`.
        //      'hu' / 'en' here hard-locks the bundle and the whole pill
        //      construction is DCE'd out.
        //   2. Runtime `availableLanguages` setting (when the build is
        //      'both'). 'hu' / 'en' pins the locale and hides the pill.
        const buildLangMode = typeof globalThis.__BUILD_LANG_MODE__ !== 'undefined'
            ? globalThis.__BUILD_LANG_MODE__
            : 'both';
        const langRestriction = buildLangMode !== 'both'
            ? buildLangMode
            : this._game?.theme?.getSetting?.('shared.settings', 'availableLanguages', 'both');
        if (langRestriction !== 'hu' && langRestriction !== 'en') {
            // Leftmost edge of the existing cluster: the back pill's left
            // edge, pulled further left by any visible help / leaderboard
            // circle.
            let clusterLeft = this._game.width - 20 - BACK_W;
            if (this._helpBtn) {
                clusterLeft = Math.min(clusterLeft, (this._game.width - HELP_BTN_X_OFFSET) - CHROME_BTN_RADIUS);
            }
            if (this._lbBtn) {
                clusterLeft = Math.min(clusterLeft, (this._game.width - LB_BTN_X_OFFSET) - CHROME_BTN_RADIUS);
            }
            const x = clusterLeft - LANG_GAP - LANG_W / 2;
            this._langBtn = createLangButton(this._game, { x, y: BTN_Y });
            this._host.addChild(this._langBtn);
        }
    }

    /**
     * Toggle visibility of the back + help buttons (matches Snake's game-over
     * behavior where these two are hidden during the post-game overlay).
     * @param {boolean} v
     */
    setVisible(v) {
        if (this._backBtn) this._backBtn.visible = v;
        if (this._langBtn) this._langBtn.visible = v;
        if (this._helpBtn) this._helpBtn.visible = v;
    }

    _showHelp() {
        if (this._helpOverlay) return;
        this._onPause();
        const { title, sections } = this._getHelp();
        this._helpOverlay = new HelpOverlay();
        this._helpOverlay.show(
            this._game.width, this._game.height,
            title, sections,
            () => this._hideHelp(),
        );
        this._overlayHost.addChild(this._helpOverlay.container);
    }

    _hideHelp() {
        if (!this._helpOverlay) return;
        this._helpOverlay.destroy();
        this._helpOverlay = null;
        this._onResume();
    }

    _showLeaderboard() {
        if (this._lbOverlay) return;
        this._onPause();
        this._lbOverlay = new LeaderboardOverlay();
        this._lbOverlay.show({
            game: this._gameId,
            onClose: () => this._hideLeaderboard(),
        });
    }

    _hideLeaderboard() {
        if (!this._lbOverlay) return;
        this._lbOverlay.destroy();
        this._lbOverlay = null;
        this._onResume();
    }

    destroy() {
        if (this._helpOverlay) {
            this._helpOverlay.destroy();
            this._helpOverlay = null;
        }
        if (this._lbOverlay) {
            this._lbOverlay.destroy();
            this._lbOverlay = null;
        }
        if (this._backBtn) {
            this._backBtn.removeAllListeners?.();
            if (this._backBtn.parent) this._backBtn.parent.removeChild(this._backBtn);
            this._backBtn = null;
        }
        if (this._langBtn) {
            this._langBtn.removeAllListeners?.();
            if (this._langBtn.parent) this._langBtn.parent.removeChild(this._langBtn);
            this._langBtn = null;
        }
        if (this._helpBtn) {
            this._helpBtn.removeAllListeners?.();
            if (this._helpBtn.parent) this._helpBtn.parent.removeChild(this._helpBtn);
            this._helpBtn = null;
        }
        if (this._lbBtn) {
            this._lbBtn.removeAllListeners?.();
            if (this._lbBtn.parent) this._lbBtn.parent.removeChild(this._lbBtn);
            this._lbBtn = null;
        }
    }
}
